import AddIcon from "@mui/icons-material/Add";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import RemoveIcon from "@mui/icons-material/Remove";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HMICachedDataContext } from "../../contexts/HMICachedDataContext/HMICachedDataContext";
import { buildManualQualityPendingKey } from "../../contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { HMIContext } from "../../contexts/HMIContext/HMIContext";
import { QueuedForReplayError, useManualQualityQueue } from "../../hooks/useManualQualityQueue";
import {
    applyManualScrapPendingOverlay,
    enumerateCompletedIntervals,
    getManualScrapForInterval,
    sumShiftManualScrap,
    IShiftIntervalOption,
} from "../../utilities/qualityIntervals";
import { useStyles } from "./QualityEntryModal.styles";

interface ISubmitState {
    submitting: boolean;
    queued: boolean;
    error: string | null;
}

const formatHmm = (iso: string): string => {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
};

const intervalLabel = (interval: IShiftIntervalOption, suffixCurrent: string, suffixPrevious: string): string => {
    const range = `${formatHmm(interval.intervalStartIso)} – ${formatHmm(interval.intervalEndIso)}`;
    const suffix = interval.kind === "current" ? suffixCurrent : suffixPrevious;
    return `${range} ${suffix}`;
};

export const QualityEntryModal = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { scrapModalOpen, closeScrapModal } = useContext(HMIContext);
    const { store } = useContext(HMICachedDataContext);
    const { submit } = useManualQualityQueue();

    const machineCenterId = store?.machineCenter?.machine_center_id;
    const plcAddress = store?.machineCenter?.plc_address;
    const lastTwoShifts = store?.lastTwoShiftsEventReports;
    const manualQualityPending = store?.manualQualityPending;

    // Snapshot the moment the modal opens, so intervals are computed against
    // the actual current time even if the modal is reopened hours after the
    // first time it was rendered. Without this, the `intervals` memo would
    // only recompute on shift-time changes — staying stuck on the
    // most-recent interval as of first render.
    const [openedAtMs, setOpenedAtMs] = useState<number>(0);
    const hasInitializedSelectionRef = useRef(false);
    const scrapInputRef = useRef<HTMLInputElement>(null);
    const hasFocusedInputRef = useRef(false);
    useEffect(() => {
        if (scrapModalOpen) {
            setOpenedAtMs(Date.now());
            setSubmitState({ error: null, queued: false, submitting: false });
        } else {
            hasInitializedSelectionRef.current = false;
            hasFocusedInputRef.current = false;
        }
    }, [scrapModalOpen]);

    const intervals = useMemo<IShiftIntervalOption[]>(() => {
        if (!scrapModalOpen || openedAtMs === 0) {
            return [];
        }
        return enumerateCompletedIntervals(
            lastTwoShifts?.previousShiftStart,
            lastTwoShifts?.previousShiftEnd,
            lastTwoShifts?.currentShiftStart,
            lastTwoShifts?.currentShiftEnd,
            new Date(openedAtMs),
            // Include the in-progress current-shift hour so it's selectable
            // and becomes the modal's default selection. Flash logic
            // (useQualityFlash → getLatestCompletedInterval) keeps the
            // default `false` so the indicator only fires for completed hours.
            true,
        );
    }, [
        scrapModalOpen,
        openedAtMs,
        lastTwoShifts?.previousShiftStart,
        lastTwoShifts?.previousShiftEnd,
        lastTwoShifts?.currentShiftStart,
        lastTwoShifts?.currentShiftEnd,
    ]);

    // Pending-overlaid raw manual-output arrays, used everywhere we read
    // scrap values (pre-population, shift total). The raw outputs are the
    // definitive record of what the operator logged — and survive a
    // local-server restart via warm-load — so the modal reads these rather
    // than the derived calc summary.
    const overlaidCurrentManual = useMemo(
        () =>
            applyManualScrapPendingOverlay(
                lastTwoShifts?.currentShiftQualityManualScrap,
                manualQualityPending,
                machineCenterId,
                lastTwoShifts?.currentShiftStart,
                lastTwoShifts?.currentShiftEnd,
            ),
        [
            lastTwoShifts?.currentShiftQualityManualScrap,
            lastTwoShifts?.currentShiftStart,
            lastTwoShifts?.currentShiftEnd,
            manualQualityPending,
            machineCenterId,
        ],
    );
    const overlaidPreviousManual = useMemo(
        () =>
            applyManualScrapPendingOverlay(
                lastTwoShifts?.previousShiftQualityManualScrap,
                manualQualityPending,
                machineCenterId,
                lastTwoShifts?.previousShiftStart,
                lastTwoShifts?.previousShiftEnd,
            ),
        [
            lastTwoShifts?.previousShiftQualityManualScrap,
            lastTwoShifts?.previousShiftStart,
            lastTwoShifts?.previousShiftEnd,
            manualQualityPending,
            machineCenterId,
        ],
    );

    const isIntervalPending = (hourAlignedIntervalStartIso: string): boolean => {
        if (machineCenterId === undefined) {
            return false;
        }
        const key = buildManualQualityPendingKey(machineCenterId, hourAlignedIntervalStartIso);
        return Boolean(manualQualityPending?.[key]);
    };

    const [selectedKey, setSelectedKey] = useState<string>("");
    const [scrapValue, setScrapValue] = useState<string>("");
    const [submitState, setSubmitState] = useState<ISubmitState>({
        error: null,
        queued: false,
        submitting: false,
    });

    // Set the default selection once per open. Subsequent recomputes of
    // `intervals` (e.g., from shift-data polls producing new string refs)
    // must NOT clobber the user's manual selection — guarded by
    // `hasInitializedSelectionRef`. The ref is reset on modal close.
    useEffect(() => {
        if (!scrapModalOpen || hasInitializedSelectionRef.current) return;
        if (intervals.length === 0) return;
        setSelectedKey(intervals[intervals.length - 1].hourAlignedIntervalStartIso);
        hasInitializedSelectionRef.current = true;
    }, [scrapModalOpen, intervals]);

    const selectedInterval = useMemo(
        () => intervals.find((i) => i.hourAlignedIntervalStartIso === selectedKey),
        [intervals, selectedKey],
    );

    // Pre-populate the input from the (overlay-applied) raw manual outputs
    // whenever the selection changes. Reads through the overlay so a
    // still-pending submission's value is shown — operator sees their last
    // submitted number, even before the server has confirmed it.
    //
    // Deps are intentionally only `[scrapModalOpen, selectedKey]`: this is a
    // snapshot taken on modal-open and interval-change. It must NOT react to
    // `overlaidCurrentManual`/`overlaidPreviousManual` — those recompute on
    // every shift-data poll AND when `submit()` dispatches the pending
    // overlay, which would re-fire this effect mid-save and reset
    // `submitState.submitting` (unlocking the modal) and clobber the
    // operator's typed value.
    useEffect(() => {
        if (!scrapModalOpen || !selectedInterval) {
            return;
        }
        const existing = getManualScrapForInterval(
            selectedInterval.hourAlignedIntervalStartIso,
            selectedInterval.kind,
            overlaidCurrentManual,
            overlaidPreviousManual,
        );
        setScrapValue(existing === 0 ? "" : String(existing));
        setSubmitState({ error: null, queued: false, submitting: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrapModalOpen, selectedKey]);

    const persistedShiftTotal = useMemo(() => sumShiftManualScrap(overlaidCurrentManual), [overlaidCurrentManual]);

    const liveShiftTotal = useMemo(() => {
        if (!selectedInterval || selectedInterval.kind !== "current") {
            return persistedShiftTotal;
        }
        const existing = getManualScrapForInterval(
            selectedInterval.hourAlignedIntervalStartIso,
            "current",
            overlaidCurrentManual,
            undefined,
        );
        const parsed = parseInt(scrapValue, 10);
        const next = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
        return persistedShiftTotal - existing + next;
    }, [persistedShiftTotal, selectedInterval, scrapValue, overlaidCurrentManual]);

    const validationError = useMemo<string | null>(() => {
        const trimmed = scrapValue.trim();
        // Empty is the zero/placeholder state — valid, treated as 0.
        const parsed = trimmed === "" ? 0 : Number(trimmed);
        if (!Number.isInteger(parsed) || parsed < 0) {
            return t("quality_entry_modal.errors.non_negative_integer");
        }
        return null;
    }, [scrapValue, t]);

    const existingValueForSelection = selectedInterval
        ? getManualScrapForInterval(
              selectedInterval.hourAlignedIntervalStartIso,
              selectedInterval.kind,
              overlaidCurrentManual,
              overlaidPreviousManual,
          )
        : 0;
    const isNoOpEdit = validationError === null && Number(scrapValue) === existingValueForSelection;
    const selectedIntervalIsPending =
        !!selectedInterval && isIntervalPending(selectedInterval.hourAlignedIntervalStartIso);
    const canSubmit =
        !!selectedInterval &&
        validationError === null &&
        !isNoOpEdit &&
        machineCenterId !== undefined &&
        !!plcAddress &&
        !submitState.submitting;

    const handleSubmit = async () => {
        if (!canSubmit || !selectedInterval || machineCenterId === undefined || !plcAddress) {
            return;
        }
        setSubmitState({ error: null, queued: false, submitting: true });
        try {
            const response = await submit({
                intervalStartIso: selectedInterval.hourAlignedIntervalStartIso,
                machineCenterId,
                plcAddress,
                value: Number(scrapValue),
            });
            if (response.success) {
                closeScrapModal();
            } else {
                // Server explicitly rejected — keep the modal open with the
                // error so the operator can adjust and retry.
                setSubmitState((prev) => ({
                    ...prev,
                    error: response.error ?? t("quality_entry_modal.errors.submit_failed"),
                    queued: false,
                }));
            }
        } catch (err) {
            // Either a publish failure or a known-disconnected skip
            // (QueuedForReplayError). Either way the entry is queued by
            // useManualQualityQueue — close the modal optimistically; the
            // pending overlay surfaces the value on the gauges and the replay
            // system retries on reconnect.
            if (!(err instanceof QueuedForReplayError)) {
                // tslint:disable-next-line: no-console
                console.error("Manual quality submit failed — queued for replay:", err);
            }
            setSubmitState((prev) => ({ ...prev, error: null, queued: true }));
            closeScrapModal();
        } finally {
            // Single guaranteed reset of `submitting` — regardless of which
            // branch ran (or any branch added later). This is what makes the
            // during-save modal lock impossible to get permanently stuck in.
            setSubmitState((prev) => ({ ...prev, submitting: false }));
        }
    };

    const handleClose = () => {
        if (submitState.submitting) {
            return;
        }
        setSubmitState({ error: null, queued: false, submitting: false });
        closeScrapModal();
    };

    return (
        <Dialog
            open={scrapModalOpen}
            onClose={handleClose}
            fullWidth={true}
            maxWidth="sm"
            disableEscapeKeyDown={submitState.submitting}
            TransitionProps={{
                onEntered: () => {
                    if (hasFocusedInputRef.current) return;
                    scrapInputRef.current?.focus();
                    hasFocusedInputRef.current = true;
                },
            }}
        >
            <DialogTitle className={classes.dialogTitle}>{t("quality_entry_modal.title")}</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <div className={classes.fieldRow}>
                    <Typography className={classes.fieldLabel}>{t("quality_entry_modal.interval_label")}</Typography>
                    <Select
                        className={classes.intervalSelect}
                        value={selectedKey}
                        onChange={(e) => setSelectedKey(String(e.target.value))}
                        disabled={intervals.length === 0 || submitState.submitting}
                    >
                        {intervals.map((interval) => {
                            const pending = isIntervalPending(interval.hourAlignedIntervalStartIso);
                            return (
                                <MenuItem
                                    key={interval.hourAlignedIntervalStartIso + interval.intervalEndIso}
                                    value={interval.hourAlignedIntervalStartIso}
                                    className={pending ? classes.dropdownItemWithPending : undefined}
                                >
                                    <span className={classes.dropdownItemLabel}>
                                        {intervalLabel(
                                            interval,
                                            t("quality_entry_modal.interval_current_shift_suffix"),
                                            t("quality_entry_modal.interval_previous_shift_suffix"),
                                        )}
                                    </span>
                                    {pending && (
                                        <CloudSyncIcon className={classes.dropdownItemPendingIcon} fontSize="small" />
                                    )}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {intervals.length === 0 && (
                        <Typography className={classes.errorText}>
                            {t("quality_entry_modal.errors.no_intervals")}
                        </Typography>
                    )}
                </div>

                <div className={classes.fieldRow}>
                    <Typography className={classes.fieldLabel}>{t("quality_entry_modal.scrap_input_label")}</Typography>
                    <TextField
                        className={classes.scrapInput}
                        type="number"
                        value={scrapValue}
                        placeholder="0"
                        inputRef={scrapInputRef}
                        onChange={(e) => setScrapValue(e.target.value)}
                        inputProps={{ min: 0, step: 1 }}
                        disabled={submitState.submitting || !selectedInterval}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" className={classes.scrapAdornment}>
                                    <IconButton
                                        className={classes.scrapAdjustButton}
                                        onClick={() => {
                                            const parsed = parseInt(scrapValue, 10);
                                            const current = Number.isFinite(parsed) ? parsed : 0;
                                            const next = Math.max(0, current - 1);
                                            setScrapValue(next === 0 ? "" : String(next));
                                        }}
                                        disabled={
                                            submitState.submitting ||
                                            !selectedInterval ||
                                            (Number.isFinite(parseInt(scrapValue, 10))
                                                ? parseInt(scrapValue, 10)
                                                : 0) <= 0
                                        }
                                        aria-label={t("quality_entry_modal.decrease_scrap")}
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                    <IconButton
                                        className={classes.scrapAdjustButton}
                                        onClick={() => {
                                            const parsed = parseInt(scrapValue, 10);
                                            const current = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
                                            setScrapValue(String(current + 1));
                                        }}
                                        disabled={submitState.submitting || !selectedInterval}
                                        aria-label={t("quality_entry_modal.increase_scrap")}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {validationError && <Typography className={classes.errorText}>{validationError}</Typography>}
                </div>

                <div className={classes.shiftTotalRow}>
                    <Typography className={classes.shiftTotalLabel}>
                        {t("quality_entry_modal.shift_total_label")}
                    </Typography>
                    <Typography className={classes.shiftTotalValue}>{liveShiftTotal}</Typography>
                </div>

                {selectedIntervalIsPending && (
                    <Alert
                        className={classes.pendingSyncNotice}
                        severity="warning"
                        icon={<CloudSyncIcon fontSize="small" />}
                    >
                        {t("quality_entry_modal.pending_sync")}
                    </Alert>
                )}
                {submitState.queued && (
                    <Typography className={classes.queuedNotice}>{t("quality_entry_modal.queued")}</Typography>
                )}
                {submitState.error && <Typography className={classes.errorText}>{submitState.error}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button className={classes.cancelButton} onClick={handleClose} disabled={submitState.submitting}>
                    {t("quality_entry_modal.cancel")}
                </Button>
                <Button
                    className={classes.submitButton}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    {t("quality_entry_modal.submit")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
